import React, { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { wa, getCampaign } from "../mock";
import { trackEvent, trackAdsConversion, ADS_QUOTE_LABEL } from "../lib/analytics";

export function InquiryForm({ campaign = "", heading = "Get a quotation", requirementPlaceholder }) {
  const [form, setForm] = useState({ name: "", company: "", phone: "", city: "", requirement: "", website: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please add your name and phone number."); return; }
    setBusy(true);
    const msg = `Hi Kapatia, enquiry from ${form.name}${form.company ? " (" + form.company + ")" : ""}. Phone: ${form.phone}. City: ${form.city || "-"}. Requirement: ${form.requirement || "-"}`;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "website", campaign: campaign || getCampaign() }),
      });
      if (!r.ok) throw new Error("failed");
      trackEvent("form_submission", { form_name: campaign || "inquiry", city: form.city || undefined, page_path: window.location.pathname });
      trackEvent("generate_lead", { form_name: campaign || "inquiry", city: form.city || undefined, page_path: window.location.pathname });
      trackAdsConversion(ADS_QUOTE_LABEL);
      toast.success("Enquiry received! We'll respond shortly. Opening WhatsApp\u2026");
      setForm({ name: "", company: "", phone: "", city: "", requirement: "", website: "" });
    } catch (_) {
      toast.warning("Saved \u2014 opening WhatsApp so your enquiry reaches us.");
    } finally {
      setBusy(false);
      // Automatic WhatsApp handoff after submit is NOT a deliberate click -> no whatsapp_click event.
      setTimeout(() => { window.open(wa(msg), "_blank", "noopener"); }, 600);
    }
  };

  const fields = [
    { k: "name", ph: "Your name *", req: true, type: "text", ac: "name" },
    { k: "phone", ph: "Phone / WhatsApp *", req: true, type: "tel", ac: "tel" },
    { k: "company", ph: "Company (optional)", type: "text", ac: "organization" },
    { k: "city", ph: "City (Noida / Delhi / Gurgaon / Bangalore)", type: "text" },
  ];

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="grid gap-3 rounded-2xl border bg-white p-6"
          style={{ borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
      <h2 data-testid="inquiry-heading" className="font-heading text-[20px] font-semibold" style={{ color: "var(--green-950)" }}>{heading}</h2>
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="inquiry-website"
             value={form.website} onChange={set("website")} name="website"
             style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
      {fields.map((f) => (
        <input key={f.k} type={f.type} value={form[f.k]} onChange={set(f.k)} required={f.req} placeholder={f.ph}
               autoComplete={f.ac} data-testid={`inquiry-${f.k}`}
               className="w-full rounded-lg border bg-white px-3.5 py-3 font-sans text-[15px] outline-none focus:ring-4"
               style={{ borderColor: "#d8c8af", color: "var(--text)" }} />
      ))}
      <textarea rows={3} value={form.requirement} onChange={set("requirement")} data-testid="inquiry-requirement"
                placeholder={requirementPlaceholder || "Tell us what you want to sell or buy \u2014 item, quantity, condition"}
                className="w-full rounded-lg border bg-white px-3.5 py-3 font-sans text-[15px] outline-none focus:ring-4"
                style={{ borderColor: "#d8c8af", color: "var(--text)" }} />
      <button type="submit" disabled={busy} data-testid="inquiry-submit"
              className="kp-button inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-heading font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #ff3f1f 0%, var(--orange-500) 58%, #ff9f1c 100%)", boxShadow: "0 16px 30px rgba(230,81,22,0.26)" }}>
        <Send size={18} /> {busy ? "Sending\u2026" : "Submit Enquiry"}
      </button>
    </form>
  );
}
